import {
  getCountry,
  type CountryIso2,
  type ParsedCountry,
} from "react-international-phone";

/** ISO 3166-1 alpha-2 country code (uppercase), e.g. "IN". */
export type PhoneCountryCode = string;

export const DEFAULT_PHONE_COUNTRY: PhoneCountryCode = "IN";

export const PREFERRED_PHONE_COUNTRIES: CountryIso2[] = [
  "in",
  "us",
  "ca",
  "gb",
  "ae",
  "au",
  "sg",
  "np",
  "pk",
  "bd",
];

/** India-compatible alias used by donate checkout legacy checks. */
export const INDIA_MOBILE_REGEX = /^[6-9]\d{9}$/;

export function toCountryIso2(
  code: string | undefined | null
): CountryIso2 {
  const trimmed = code?.trim();
  if (!trimmed) {
    return DEFAULT_PHONE_COUNTRY.toLowerCase() as CountryIso2;
  }
  return trimmed.toLowerCase() as CountryIso2;
}

export function toPhoneCountryCode(
  iso2: string | undefined | null
): PhoneCountryCode {
  const trimmed = iso2?.trim();
  if (!trimmed) {
    return DEFAULT_PHONE_COUNTRY;
  }
  return trimmed.toUpperCase();
}

export function getParsedPhoneCountry(
  code: string | undefined | null
): ParsedCountry | undefined {
  return getCountry({
    field: "iso2",
    value: toCountryIso2(code),
  });
}

function maskDigitCount(country: ParsedCountry | undefined): number {
  if (!country) {
    return 15;
  }
  const format = country.format;
  const mask =
    typeof format === "string"
      ? format
      : format && typeof format === "object" && "default" in format
        ? String(format.default)
        : "............";
  const count = (mask.match(/\./g) ?? []).length;
  return count > 0 ? count : 15;
}

export function getMobileMaxLength(
  countryCode: string | undefined | null = DEFAULT_PHONE_COUNTRY
): number {
  const upper = toPhoneCountryCode(countryCode);
  if (upper === "IN") {
    return 10;
  }
  return maskDigitCount(getParsedPhoneCountry(upper));
}

export function sanitizeMobileNumber(
  value: string,
  countryCode: string | undefined | null = DEFAULT_PHONE_COUNTRY
): string {
  return value.replace(/\D/g, "").slice(0, getMobileMaxLength(countryCode));
}

export function buildE164PhoneValue(
  nationalNumber: string,
  countryCode: string | undefined | null = DEFAULT_PHONE_COUNTRY
): string {
  const country = getParsedPhoneCountry(countryCode);
  if (!country) {
    return nationalNumber.replace(/\D/g, "");
  }
  const digits = sanitizeMobileNumber(nationalNumber, countryCode);
  if (!digits) {
    return `+${country.dialCode}`;
  }
  return `+${country.dialCode}${digits}`;
}

export function extractNationalNumber(
  e164OrInput: string,
  dialCode: string
): string {
  const digits = e164OrInput.replace(/\D/g, "");
  const codeDigits = dialCode.replace(/\D/g, "");
  if (digits.startsWith(codeDigits)) {
    return digits.slice(codeDigits.length);
  }
  return digits;
}

export function isValidMobileNumber(
  value: string,
  countryCode: string | undefined | null = DEFAULT_PHONE_COUNTRY,
  options?: { required?: boolean }
): boolean {
  const required = options?.required ?? true;
  const digits = sanitizeMobileNumber(value, countryCode);
  if (!digits) {
    return !required;
  }

  const upper = toPhoneCountryCode(countryCode);
  if (upper === "IN") {
    return INDIA_MOBILE_REGEX.test(digits);
  }

  const expected = getMobileMaxLength(upper);
  if (digits.length < Math.min(7, expected) || digits.length > expected) {
    return false;
  }
  return /^\d+$/.test(digits);
}

/** @deprecated Prefer getParsedPhoneCountry — kept for older call sites. */
export type PhoneCountry = {
  code: PhoneCountryCode;
  name: string;
  dialCode: string;
  maxLength: number;
  pattern: RegExp;
};

export function getPhoneCountry(
  code: string | undefined | null
): PhoneCountry {
  const parsed = getParsedPhoneCountry(code);
  const resolved = toPhoneCountryCode(code);
  const maxLength = getMobileMaxLength(resolved);
  return {
    code: resolved,
    name: parsed?.name ?? resolved,
    dialCode: parsed ? `+${parsed.dialCode}` : "+91",
    maxLength,
    pattern:
      resolved === "IN"
        ? INDIA_MOBILE_REGEX
        : new RegExp(`^\\d{${Math.min(7, maxLength)},${maxLength}}$`),
  };
}
